import { Card, Suit, Player } from '../types';
import { getPossibleCaptures, isScopaMove } from './gameUtils';
import { GAME_CONSTANTS } from './constants';

export interface AIMove {
  card: Card;
  capture: Card[] | null;
}

export interface AIConfig {
  players: Player[];
  table: Card[];
  currentPlayerIndex: number;
  deck: Card[];
  numPlayers: number;
  difficulty?: 'easy' | 'medium' | 'hard';
}

// ============================================================
// EVALUATION — pure Scopa rules, no heuristics
// ============================================================

// Real game scoring — used for game logic
function scoreCapture(card: Card, captured: Card[], table: Card[]): number {
  let s = 0;

  // More cards captured = better
  s += captured.length;

  // The played card counts too
  s += 1;

  // Scopa: you empty the table → +3
  if (isScopaMove(card, table, captured)) s += 3;

  // Special coins cards when captured
  for (const c of captured) {
    if (c.suit === Suit.COINS) {
      if (c.value === 7) s += GAME_CONSTANTS.SETTEBELLO_BONUS;   // Settebello
      else if (c.value === 10) s += GAME_CONSTANTS.DIECI_DENARI_BONUS; // Dieci denari
      else if (c.value === 2) s += GAME_CONSTANTS.DUE_DENARI_PENALTY;  // Due denari
    }
  }

  // The played card if it's a special coin
  if (card.suit === Suit.COINS) {
    if (card.value === 7) s += GAME_CONSTANTS.SETTEBELLO_BONUS;
    else if (card.value === 10) s += GAME_CONSTANTS.DIECI_DENARI_BONUS;
    else if (card.value === 2) s += GAME_CONSTANTS.DUE_DENARI_PENALTY;
  }

  return s;
}

// Internal AI scoring — same rules but with extra CPU-specific aversion to 2 denari
function scoreCaptureAI(card: Card, captured: Card[], table: Card[]): number {
  let s = scoreCapture(card, captured, table);

  // CPU gets a massive internal penalty for touching the 2 of denari
  // This is invisible to the player — the real score stays -5
  const hasDueDenari = captured.some(c => c.suit === Suit.COINS && c.value === 2);
  if (hasDueDenari) s -= 15;
  if (card.suit === Suit.COINS && card.value === 2) s -= 15;

  return s;
}

function scoreNoCapture(card: Card): number {
  // Negative score: you're giving cards to the table
  // Prefer to play low value cards (less useful to opponent)
  // Coins on table without capture is bad if opponent can take it next turn
  if (card.suit === Suit.COINS) {
    if (card.value >= 6) return -3;  // Don't give high coins to opponent
    return -1;
  }
  return card.value * -0.3; // Lower is better to keep low
}

// ============================================================
// STRATEGIES: EASY, MEDIUM, HARD
// ============================================================

export async function getBestMove(config: AIConfig): Promise<AIMove | null> {
  const { players, table, currentPlayerIndex, difficulty = 'medium' } = config;
  const hand = players[currentPlayerIndex].hand;

  if (hand.length === 0) return null;

  // 1. Gather all possible valid moves
  const validMoves: AIMove[] = [];
  for (const card of hand) {
    const captures = getPossibleCaptures(card, table);
    if (captures.length > 0) {
      for (const cap of captures) {
        validMoves.push({ card, capture: cap });
      }
    } else {
      validMoves.push({ card, capture: null });
    }
  }

  if (validMoves.length === 0) return null;

  // --- EASY DIFFICULTY: 50% chance of playing a random valid move ---
  if (difficulty === 'easy') {
    if (Math.random() < 0.5) {
      const randomIndex = Math.floor(Math.random() * validMoves.length);
      return validMoves[randomIndex];
    }
    // Else, falls through to greedy (Medium) strategy below
  }

  // --- HARD DIFFICULTY: card-tracking heuristic with positional scoring ---
  if (difficulty === 'hard') {
    // Heuristic: tracks the full 40-card deck, evaluates captures, Scopa/Sbarazzo
    // potential, suit distribution, and table-control scoring. No tree search, no
    // minimax — each candidate move is scored independently by combining these
    // weighted factors.
    // Generate all 40 cards of the Napoletane deck
    const suits = [Suit.COINS, Suit.CUPS, Suit.SWORDS, Suit.CLUBS];
    const all40Cards: Card[] = [];
    for (const suit of suits) {
      for (let val = 1; val <= 10; val++) {
        all40Cards.push({ id: `${suit}-${val}`, value: val, suit });
      }
    }

    // Identify unknown cards (not in hand, on table, or in anyone's captured pile)
    const seenCardIds = new Set<string>();
    hand.forEach(c => seenCardIds.add(c.id));
    table.forEach(c => seenCardIds.add(c.id));
    players.forEach(p => p.captured.forEach(c => seenCardIds.add(c.id)));
    const unknownCards = all40Cards.filter(c => !seenCardIds.has(c.id));

    let bestMove: AIMove | null = null;
    let bestScore = -Infinity;

    for (const move of validMoves) {
      const { card, capture } = move;
      let score = 0;

      // Base greedy score
      if (capture) {
        score += scoreCaptureAI(card, capture, table);
      } else {
        score += scoreNoCapture(card);
      }

      // Calculate resulting table
      let newTable: Card[] = [];
      if (capture) {
        newTable = table.filter(tc => !capture.some(cc => cc.id === tc.id));
      } else {
        newTable = [...table, card];
      }

      // Safe state: table is cleared, opponent cannot do Scopa/Sbarazzo
      if (newTable.length === 0) {
        score += 2.0;
      } else {
        // Evaluate opponent's possible responses using unknown cards
        let scopaRisk = 0;
        let sbarazzoRisk = 0;
        const totalUnknown = unknownCards.length || 1;

        // Check if there are any Assi (value 1) left in the unknown cards
        const remainingAces = unknownCards.filter(c => c.value === 1).length;

        // Sum of cards on the new table
        const tableSum = newTable.reduce((sum, c) => sum + c.value, 0);
        const coinsOnTable = newTable.filter(c => c.suit === Suit.COINS).length;

        // If opponent plays an Asso on a non-empty table, it's a Sbarazzo
        if (remainingAces > 0) {
          // Risk is proportional to the number of remaining aces and the value of cards on the table
          sbarazzoRisk += (remainingAces / totalUnknown) * (tableSum + coinsOnTable * 2) * 2;
        }

        // Check if any unknown card can make a Scopa on newTable
        for (const uCard of unknownCards) {
          const uCaptures = getPossibleCaptures(uCard, newTable);
          const canScopa = uCaptures.some(uc => uc.length === newTable.length);
          if (canScopa) {
            scopaRisk += 1.5; // accumulate risk
          }
        }

        score -= (scopaRisk + sbarazzoRisk);

        // Penalty for playing 2 of Coins if it can be easily captured by a 2 in unknown cards
        if (!capture && card.suit === Suit.COINS && card.value === 2) {
          const hasTwoInUnknown = unknownCards.some(c => c.value === 2);
          if (hasTwoInUnknown) {
            score -= 10.0;
          }
        }
      }

      // Strategic Ace handling:
      if (card.value === 1) {
        if (table.length === 0) {
          // Strongly discourage playing Asso on empty table
          score -= 8.0;
        } else {
          // Encourage playing Asso when table is large/juicy
          const tableVal = table.reduce((sum, c) => sum + c.value, 0);
          score += tableVal * 0.1;
        }
      }

      if (score > bestScore) {
        bestScore = score;
        bestMove = move;
      }
    }

    return bestMove;
  }

  // --- MEDIUM / DEFAULT GREEDY STRATEGY ---
  let bestMove: AIMove | null = null;
  let bestScore = -Infinity;

  for (const move of validMoves) {
    const { card, capture } = move;
    if (capture) {
      const sc = scoreCaptureAI(card, capture, table);
      if (sc > bestScore) {
        bestScore = sc;
        bestMove = move;
      }
    } else {
      const sc = scoreNoCapture(card);
      if (sc > bestScore) {
        bestScore = sc;
        bestMove = move;
      }
    }
  }

  return bestMove;
}